/* eslint-disable no-undef */
import API from "constants/api";
import { SET_SAVED } from "constants/actionTypes";
import Predicates from "constants/predicates";
import Types from "constants/types";

import {
  getFormData,
  resToJsonHandleErrors,
  actionError,
} from "helpers/actionHelpers";
import { redirectIFrame } from "helpers/iframeHelpers";
import { checkUniquenessAndPromptUser } from "actions/validationActions";
import { convertViewToFormat } from "helpers/dataHelpers";

/**
 * Checks if the fields used for statement PDF generation have been edited
 */
export const checkStatementPdfFieldsEdited =
  (formRef) => (dispatch, getState) => {
    const state = getState();
    const fields = state.field.fields;

    let statementFieldsEdited = false;
    for (let field of Object.values(state.field.fields)) {
      if (field?.props?.statementPdfField) {
        // Copied from objectValidation
        const editedValues = field.values.filter((v) => v !== "");
        const originalValues = state.field.originalValues[field.predicate];

        const len = editedValues.length;
        const prevLen = originalValues.length;

        var isObjectField = false;
        if (len > 0) {
          if (typeof editedValues[0] === "object") {
            isObjectField = true;
          }
        }
        if (prevLen > 0) {
          if (typeof originalValues[0] === "object") {
            isObjectField = true;
          }
        }

        if (len !== prevLen) {
          if (len > prevLen) {
            if (!isObjectField) {
              statementFieldsEdited = true;
              break;
            }
          } else {
            if (!isObjectField) {
              statementFieldsEdited = true;
              break;
            }
          }
          continue;
        }

        for (let i = 0; i < len; i++) {
          const edited = editedValues[i];
          const original = originalValues[i];

          if (typeof edited === "object") {
            if (edited.uri !== original.uri) {
              statementFieldsEdited = true;
              break;
            }
          } else if (edited !== original) {
            statementFieldsEdited = true;
            break;
          }
        }
      }
    }
    return statementFieldsEdited;
  };

/**
 * POST to CreateArchivedObjectController with geometry handling
 */
export const createObjectWithGeometry = (message) => (dispatch, getState) => {
  const state = getState();

  const formData = getFormData(state.field);
  formData.set("parentUri", state.object.parentUri);
  formData.set("view", state.object.view);
  formData.set("templateItem", state.object.uri);

  if (state.object.namedTemplate) {
    formData.set("namedTemplate", state.object.namedTemplate);
  }
  if (
    state.object.objectCount &&
    state.object.objectCount != "" &&
    state.object.objectCount != "1"
  ) {
    formData.set("objectsToCreate", state.object.objectCount);
  }
  const view = state.object.view;
  const fields = state.field.fields;
  const oldFields = state.field.originalValues;
  checkUniquenessAndPromptUser(
    convertViewToFormat(view),
    fields,
    oldFields,
    "non-existing-uri",
    message
  ).then((allowed) => {
    if (allowed) {
      return fetch(API.DAM.CREATE_OBJECT, {
        method: "POST",
        body: formData,
      })
        .then(resToJsonHandleErrors)
        .then((res) => {
          if (res.uri) {
            if (res.createdURIs) {
              const promises = [];
              const fields = state.field.fields;
              res.createdURIs.forEach((uri) => {
                promises.push(saveGeometry(fields, uri));
              });
              Promise.allSettled(promises).then(() =>
                redirectIFrame(`${API.DAM.EDIT_OBJECT}?uri=${res.uri}`)
              );
            } else {
              const uri = res.uri;
              const fields = state.field.fields;
              saveGeometry(fields, uri).then(() =>
                redirectIFrame(`${API.DAM.EDIT_OBJECT}?uri=${res.uri}`)
              );
            }
          } else {
            actionError({ message: "saveFailed" });
          }
        });
    }
  });
};

/**
 * POST to EditObjectController with geometry handling
 */
export const editObjectWithGeometry =
  (parameters, message) => (dispatch, getState) => {
    const state = getState();
    const formData = getFormData(state.field);
    formData.set("uri", state.object.uri);

    const primaryVersion = state.object.primaryVersion;
    if (primaryVersion) {
      formData.set(Predicates.ARCHIVE.PRIMARY_VERSION, primaryVersion);
    }
    parameters = new URLSearchParams(parameters).toString();
    const view = state.object.view;
    const fields = state.field.fields;
    const oldFields = state.field.originalValues;
    checkUniquenessAndPromptUser(
      convertViewToFormat(view),
      fields,
      oldFields,
      state.object.uri,
      message
    ).then((res) => {
      if (res) {
        return fetch(`${API.DAM.EDIT_OBJECT}?${parameters}`, {
          method: "POST",
          body: formData,
        })
          .then(resToJsonHandleErrors)
          .then((result) => {
            if (result.returnUrl) {
              let uri = result.returnUrl;
              uri = uri.substring(uri.indexOf("http://"));
              const fields = state.field.fields;
              saveGeometry(fields, uri).then(() => {
                dispatch({ type: SET_SAVED });
                redirectIFrame(result.returnUrl);
              });
            } else {
              actionError({ message: "saveFailed" });
            }
          });
      }
    });
  };

/**
 * Call EditLocationController to process newly added geometries,
 * to ensure they are saved as the correct literal type
 * @param {*} fields array or object of fields of the newly added object or resource
 * @param {*} uri the URI of the object or resource saved
 */
export const saveGeometry = (fields, uri) => {
  let geometryValues = [];
  let geometryPredicate = "";
  if (!Array.isArray(fields)) {
    fields = Object.values(fields);
  }
  fields.forEach((field) => {
    if (field.type === Types.SPECTRUM.GEOMETRY) {
      geometryValues = [...field.values];
      geometryPredicate = field.predicate;
    }
  });
  if (geometryPredicate === "") {
    return Promise.resolve();
  }
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uri: uri,
      predicate: geometryPredicate,
      locations: geometryValues,
    }),
  };
  return fetch(`${API.EDIT_LOCATION}`, requestOptions).then(
    (response) => response
  );
};

/**
 * Split a string of comma-separated points into an array of points as strings
 * @param {*} position
 * @returns
 */
export const splitPosition = (position) => {
  return position.split(", ");
};

/**
 * For each element in an array of points as space-separated strings, split into an array [lat, lng]
 * @param {*} pointStrings array of points as space-separated strings
 * @param {*} polygon if true, last point is set as a copy of first point, as required for Polygon literal in Sense
 * @returns array of points in the form [lat, lng]
 */
export const splitPoints = (pointStrings, polygon) => {
  let points = [];
  pointStrings.forEach((p) => points.push(p.split(" ")));
  if (polygon) {
    points.pop();
  }
  return points;
};

/**
 * Return the name of a layer from mapLayers.js for the current locale. If the layer has a
 * help text configured, include an icon with the text embedded as a tooltip
 * @param {*} layerDetails configuration object representing a layer from mapLayers.js
 * @param {*} locale current locale code
 */
export const getLayerLabel = (layerDetails, locale) => {
  const help = layerDetails[`help_${locale}`];
  if (!help) {
    return layerDetails[`name_${locale}`];
  }
  return (
    '<i class="fa fa-question-circle layer-tooltip-icon" title="' +
    help +
    '"></i> ' +
    layerDetails[`name_${locale}`]
  );
};

/**
 * Get the map bounds to fit the geometry value of the given field
 * @param {*} field
 * @returns LatLngBounds
 */
export const getGeometryBounds = (field) => {
  let pointStrings = [];
  field.values.forEach((string) => {
    pointStrings = pointStrings.concat(splitPosition(string));
  });
  let points = splitPoints(pointStrings, false);
  let latLngs = [];
  points.forEach((point) => {
    latLngs.push(new L.LatLng(point[0], point[1]));
  });
  if (latLngs.length == 1) {
    return latLngs[0].toBounds(500);
  }
  return L.latLngBounds(latLngs);
};
